import React, { useEffect, useRef, useState } from "react";
import { Logo } from "assets/svg";
import { useDispatch, useSelector } from "react-redux";
import { uploadImage, uploadPdf } from "reducers/uploadSlice";
import { AppDispatch, RootState } from "store/store";
import { PathStoreEnum } from "common/enums/PathStore.enum";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { updateRegistrationFormData } from "reducers/informationSlice";
import { message } from "antd";
import { MAX_FILE_SIZE } from "common/constants";

const VerifyInformation: React.FC = () => {
  const abortController = new AbortController();
  const dispatch = useDispatch<AppDispatch>();
  const { uploadImageData, uploadPdfData } = useSelector(
    (state: RootState) => state.upload,
  );
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const type = searchParams.get("type");

  const [file1, setFile1] = useState<File | null>(null);
  const [file2, setFile2] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const fileInput1 = useRef<HTMLInputElement>(null);
  const fileInput2 = useRef<HTMLInputElement>(null);

  const handleFile1 = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.size > MAX_FILE_SIZE) {
        message.error("ファイルサイズは10MB以下にしてください。");
        e.target.value = "";
        return;
      }
      setFile1(file);
    }
  };

  const handleFile2 = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.size > MAX_FILE_SIZE) {
        message.error("ファイルサイズは10MB以下にしてください。");
        e.target.value = "";
        return;
      }
      setFile2(file);
    }
  };

  const handleSubmit = async () => {
    if (!file1 || !file2) {
      alert("両方のファイルを選択してください。");
      return;
    }
    setIsSubmitting(true);
    const formDataImg = new FormData();
    formDataImg.append("file", file1);
    dispatch(
      uploadImage(
        formDataImg,
        PathStoreEnum.PATH_STORE_IMAGE,
        abortController.signal,
      ),
    );

    const formDataPdf = new FormData();
    formDataPdf.append("file", file2);
    dispatch(
      uploadPdf(
        formDataPdf,
        PathStoreEnum.PATH_STORE_PDF,
        abortController.signal,
      ),
    );
  };

  useEffect(() => {
    if (uploadImageData && uploadPdfData && isSubmitting) {
      dispatch(
        updateRegistrationFormData({
          number_card_url: uploadImageData.data.path,
          corp_regist_cert_url: uploadPdfData.data.path,
        }),
      );
      navigate(`/accept-term-service?token=${token}&type=${type}`);
      setIsSubmitting(false);
    }
  }, [
    uploadImageData,
    uploadPdfData,
    isSubmitting,
    dispatch,
    navigate,
    token,
    type,
  ]);

  return (
    <main className="bg--blur box">
      <div
        className="w-full relative rounded-[20px] border-[8px] border-white bg-primary-light shadow-[0_0_15px_0_rgba(0,0,0,0.05)] flex flex-col items-center justify-center py-[52px] px-[22px]
        sm:max-w-[1020px] sm:py-[72px] sm:px-[62px]"
      >
        <Logo
          className="flex justify-center items-center w-[200px] h-[81px] aspect-[1/1] mb-[30px]
             sm:w-[300px] sm:h-[121px] sm:mb-[40px]"
        />
        <h2 className="text-[22px] not-italic font-bold text-primary mb-[15px] sm:text-[28px] sm:mb-[18px] text-center">
          本人確認書類の <br className="sm:hidden" />
          アップロード
        </h2>
        <p className="text-[15px] sm:text-[16px] leading-[160%] text-center mb-[30px] sm:mb-[40px]">
          ご本人および法人確認のため、
          <br className="sm:hidden" />
          以下の書類を両方 <br className="sm:hidden" />
          アップロードしてください。
        </p>

        {/* Section 1 */}
        <section className="w-full mb-[28px] sm:mb-[36px]">
          <h3 className="text-[18px] py-[8px] sm:py-[10px] font-bold text-primary mb-[20px] sm:text-[20px] sm:mb-[40px] flex items-center border-b border-b-neutral-border">
            <div className="w-[4px] h-[29px] bg-primary inline-block mr-[10px] sm:mr-[15px] rounded-[6px]"></div>
            <span className="bg-red-dark px-[10px] md:px-[15px] text-white text-[11px] rounded-[6px] mr-[10px] ">
              必須
            </span>
            1. マイナンバーカード <br className="sm:hidden" />
            （表面）
          </h3>
          <ul className="text-[14px] sm:text-[16px] text-black mb-[15px] sm:mb-[18px] list-disc pl-[22px]">
            <li className="list-disc marker:text-xs marker:leading-none">
              担当者ご本人のマイナンバーカード表面の画像
            </li>
            <li className="list-disc marker:text-xs marker:leading-none">
              文字・顔写真が鮮明に写っているものをご用意ください
            </li>
          </ul>
          <div className="flex items-center gap-[10px] mb-[7px]">
            <button
              type="button"
              onClick={() => fileInput1.current?.click()}
              className="min-w-[138px] text-primary px-[18px] py-[7px] rounded-[6px] text-[14px] sm:text-[15px] font-bold active:scale-95 transition-transform border-primary border-2"
            >
              ファイルを選択
            </button>
            <span
              className="text-gray-400 text-[13px] sm:text-[14px] max-w-[200px] sm:max-w-[300px] truncate"
              title={file1 ? file1.name : undefined}
            >
              {file1 ? file1.name : "選択されていません。"}
            </span>
            <input
              ref={fileInput1}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              className="hidden"
              onChange={handleFile1}
            />
          </div>
          <div className="text-[11px] sm:text-[12px]">
            ※JPG／PNG／PDF（10MBまで）
          </div>
        </section>

        {/* Section 2 */}
        <section className="w-full mb-[28px] sm:mb-[36px]">
          <h3 className="text-[18px] py-[8px] sm:py-[10px] font-bold text-primary mb-[20px] sm:text-[20px] sm:mb-[40px] flex items-center border-b border-b-neutral-border">
            <div className="w-[4px] h-[29px] bg-primary inline-block mr-[10px] sm:mr-[15px] rounded-[6px]"></div>
            <span className="bg-red-dark px-[10px] md:px-[15px] text-white text-[11px] rounded-[6px] mr-[10px] ">
              必須
            </span>
            2. 法人登記簿謄本
            <br className="sm:hidden" />
            （履歴事項全部証明書）
          </h3>
          <ul className="text-[14px] sm:text-[16px] text-black mb-[15px] sm:mb-[18px] list-disc pl-[22px]">
            <li className="list-disc marker:text-xs marker:leading-none">
              発行から3ヶ月以内のものをご用意ください
            </li>
            <li className="list-disc marker:text-xs marker:leading-none">
              スキャンまたはPDF形式で提出してください
            </li>
          </ul>
          <div className="flex items-center gap-[10px] mb-[7px]">
            <button
              type="button"
              onClick={() => fileInput2.current?.click()}
              className="min-w-[138px] text-primary px-[18px] py-[7px] rounded-[6px] text-[14px] sm:text-[15px] font-bold active:scale-95 transition-transform border-primary border-2"
            >
              ファイルを選択
            </button>
            <span
              className="text-gray-400 text-[13px] sm:text-[14px] max-w-[200px] sm:max-w-[300px] truncate"
              title={file2 ? file2.name : undefined}
            >
              {file2 ? file2.name : "選択されていません。"}
            </span>
            <input
              ref={fileInput2}
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={handleFile2}
            />
          </div>
          <div className="text-[11px] sm:text-[12px]">※PDF推奨（最大10MB）</div>
        </section>

        {/* 注意事項 */}
        <section className="w-full bg-[#F6F8FB] rounded-[10px] p-[20px] sm:px-[50px] sm:py-[30px] mb-[28px] sm:p-[22px] sm:mb-[36px]">
          <div className="text-primary font-bold text-[18px] mb-[15px] sm:text-[20px]">
            注意事項
          </div>
          <ul className="text-[13px] sm:text-[15px] text-black list-disc pl-[22px]">
            <li className="list-disc marker:text-xs marker:leading-none">
              ご提出いただいた書類は審査のみに使用します
            </li>
            <li className="list-disc marker:text-xs marker:leading-none">
              審査には数営業日かかる場合があります
            </li>
          </ul>
        </section>

        {/* Buttons */}
        <div className="w-full flex flex-col gap-[14px] sm:flex-row sm:gap-[22px] justify-center mb-[18px]">
          <button
            type="button"
            onClick={handleSubmit}
            className="w-full bg-primary text-white py-[15px] rounded-[6px] text-[15px] font-bold sm:w-[320px] sm:text-[16px] sm:py-[16px] sm:order-2"
          >
            書類を提出して利用規約を確認する
          </button>
          <Link
            to={`/information?token=${token}&type=${type}`}
            type="button"
            className="text-center w-full bg-[#E3ECF8] text-primary py-[15px] rounded-[6px] text-[15px] font-bold sm:w-[320px] sm:text-[16px] sm:py-[16px] sm:order-1"
          >
            企業情報の入力に戻る
          </Link>
        </div>

        {/* Footer note */}
        <div className="text-[12px] text-center w-full sm:text-[13px]">
          ※この後、本人確認書類の提出が必要です。
        </div>
      </div>
    </main>
  );
};

export default VerifyInformation;
